import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { FileVideo, Download, Clock, Calendar } from 'lucide-react';
import { MediaImage } from '@/components/ui/media-image';

interface Recording {
  id: string;
  streamId: number;
  streamTitle: string;
  title: string;
  duration: number;
  fileSize: number;
  createdAt: string;
  status: 'processing' | 'ready' | 'failed';
  downloadUrl?: string;
  thumbnailUrl?: string | null;
}

interface RecordingCardProps {
  recording: Recording;
  onClick: () => void;
}

export function RecordingCard({ recording, onClick }: RecordingCardProps) {
  const navigate = useNavigate();

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return <Badge variant="default">Ready</Badge>;
      case 'processing':
        return <Badge variant="secondary">Processing</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onClick}>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          {/* Thumbnail — use explicit url, or auto-generate from Cloudflare video ID, or fallback icon */}
          <div className="w-32 h-20 bg-slate-900 rounded overflow-hidden flex-shrink-0 relative">
            {(() => {
              const thumb = recording.thumbnailUrl ||
                (recording.id ? `https://videodelivery.net/${recording.id}/thumbnails/thumbnail.jpg` : null);
              return thumb ? (
                <MediaImage
                  src={thumb}
                  alt={recording.title}
                  className="w-full h-full object-cover"
                />
              ) : null;
            })()}
            <div className="absolute inset-0 flex items-center justify-center text-slate-600 bg-slate-900">
              <FileVideo className="w-8 h-8" />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base line-clamp-1">{recording.title}</h3>
            <p className="text-sm text-muted-foreground truncate">{recording.streamTitle}</p>
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDuration(recording.duration)}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {recording.createdAt ? new Date(recording.createdAt).toLocaleDateString() : 'Unknown'}
              </div>
            </div>
          </div>

          {/* Status & Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {getStatusBadge(recording.status)}
            <Button
              variant="outline"
              size="sm"
              disabled={!recording.downloadUrl || recording.status !== 'ready'}
              onClick={(e) => {
                e.stopPropagation();
                if (recording.downloadUrl) {
                  window.open(recording.downloadUrl, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
